<template>
    <div>
        <div class="row">
            <div class="col-md-12">
                <span class="agenda-question">{{ $t("meetings.labelSchedule") }}</span>
                <i
                    class="fal color-grey l-default-margin-s"
                    :class="{ 'fa-sync fa-spin': updatingMeeting, 'fa-check': !updatingMeeting }"
                ></i>
                <span class="color-grey l-default-margin-s">{{
                    $t("meetings.labelAutosaved")
                }}</span>
            </div>
        </div>

        <l-space :size="1" />

        <div class="row">
            <!-- Participants -->
            <div class="col-md-12">
                <l-input-typeahead
                    :label="''"
                    :placeholder="$t(`meetings.placeholderAdd`)"
                    :data="participants"
                    :on-select="typeaheadParticipantsOnSelect"
                    :on-input-change="typeaheadParticipantsOnInputChange"
                />

                <div
                    v-if="meeting.otherUsers && meeting.otherUsers.length"
                    class="other-users-container"
                >
                    <span v-for="user in googleCalendarUsers" :key="user._id">
                        <span class="label r-default-margin-s clickable">
                            {{ user.firstname }} {{ user.lastname }}
                        </span>
                    </span>
                    <span v-for="user in localUsers" :key="user._id">
                        <span
                            class="label label-primary r-default-margin-s clickable"
                            @click="() => removeParticipant(user)"
                            @keyup.enter="() => removeParticipant(user)"
                        >
                            {{ user.firstname }} {{ user.lastname }}
                            <i class="fal fa-trash l-default-margin-s"></i>
                        </span>
                    </span>
                </div>
            </div>
            <!--/Participants -->
        </div>

        <l-space :size="2" />

        <div class="row">
            <!-- Title -->
            <div class="col-md-12">
                <l-input-text
                    ref="input-title"
                    v-model="meeting.title"
                    type="text"
                    optional
                    :label="$t(`meetings.labelTitle`)"
                    :placeholder="$t(`meetings.labelTitle`)"
                    data-test-id="meeting-edit-title"
                    :readonly="isLinkedToGoogle"
                />
            </div>
            <!--/Title -->
        </div>

        <l-space :size="2" />

        <div class="row">
            <!-- Frequency -->
            <div v-if="!isLinkedToGoogle" class="col-md-3">
                <l-select
                    id="frequency"
                    v-model="meeting.cycle.frequency"
                    :label="$t(`meetings.labelFrequency`)"
                    :options="optionsFrequency"
                    data-test-id="meeting-frequency"
                />
            </div>
            <!--/Frequency -->

            <!-- Date -->
            <div class="col-md-3">
                <l-date-picker
                    wrapper-class="date-picker-wrapper"
                    :label="$t(`meetings.labelFirstTime`)"
                    :placeholder="''"
                    :value="new Date(meeting.cycle.nextTime)"
                    :on-date-change="updateNextDate"
                    data-test-id="meeting-nextTime"
                    :readonly="isLinkedToGoogle"
                />
            </div>

            <div class="col-md-4">
                <l-time-picker
                    :value="new Date(meeting.cycle.nextTime)"
                    :use-meridian-time="useMeridianTime"
                    :readonly="isLinkedToGoogle"
                    @change="updateNextTime"
                />
            </div>

            <div class="col-md-2">
                <l-input-text
                    ref="input-title"
                    v-model="meeting.cycle.duration"
                    type="text"
                    :label="$t(`meetings.labelDuration`)"
                    :placeholder="$t(`meetings.labelDuration`)"
                    data-test-id="meeting-duration"
                    :readonly="isLinkedToGoogle"
                />
            </div>
            <!--/Date -->
        </div>

        <!-- Calendar settings -->
        <div class="tw-flex tw-space-x-2">
            <!-- Enable Google Calendar Integration -->
            <div v-if="!user.isGoogleCalendarSyncEnabled" class="tw-flex-grow-0">
                <button class="btn btn-default btn-raised" @click="authGoogleCalendar">
                    {{ $t("profile.enableGoogleCalendarIntegration") }}
                </button>
            </div>

            <!-- Link Google Event -->
            <div
                v-if="user.isGoogleCalendarSyncEnabled && !isLinkedToGoogle"
                class="tw-flex-grow-0"
            >
                <l-google-calendar-event-select
                    :on-select="linkGoogleCalendar"
                    :on-error="googleEventLinkError"
                />
            </div>

            <!-- Edit Google Event -->
            <div v-if="user.isGoogleCalendarSyncEnabled && isLinkedToGoogle" class="tw-flex-grow-0">
                <button
                    target="_blank"
                    rel="noreferrer noopener"
                    class="btn btn-primary btn-raised"
                    @click="goToEvent()"
                    @keypress.enter="goToEvent()"
                >
                    {{ $t("meetings.editInGoogleCalendar") }}
                </button>
            </div>

            <!-- Unlink Google Event -->
            <div v-if="user.isGoogleCalendarSyncEnabled && isLinkedToGoogle" class="tw-flex-grow-0">
                <button class="btn btn-default" @click="unlinkGoogleCalendar()">
                    {{ $t("meetings.unlinkGoogleCalendarEvent") }}
                </button>
            </div>

            <!-- Other calendars -->
            <div v-if="!user.isGoogleCalendarSyncEnabled && !calendar.url" class="tw-flex-grow-0">
                <button
                    v-if="!calendar.url"
                    class="btn btn-default btn-raised"
                    @click="createCalendar()"
                    @keyup.enter="createCalendar()"
                >
                    <i v-if="updatingCalendar" class="fal fa-sync fa-spin"></i>
                    <span>{{ $t("meetings.btnCreateCalendar") }}</span>
                </button>
            </div>

            <div v-if="!user.isGoogleCalendarSyncEnabled && calendar.url" class="tw-flex-1">
                <l-input-text
                    v-if="calendar.url"
                    v-model="calendar.url"
                    type="text"
                    :label="''"
                    readonly
                    hide-help-block
                    data-test-id="meeting-calendar-url"
                />
                <span v-if="calendarURLCopied" class="explanation">
                    <span class="l-default-margin-s">
                        <a
                            href="https://leapsome.zendesk.com/hc/en-us/articles/360002572477"
                            target="_blank"
                            rel="noopener"
                            >{{ $t("meetings.calendarCopied") }}
                        </a>
                    </span>
                </span>
            </div>
            <!--/Calendar integration -->
        </div>

        <div v-if="user.isGoogleCalendarSyncEnabled">
            <hr />
            <i class="fal fa-exclamation-square r-default-margin-s"></i>
            {{ $t("meetings.linkedEventsWarning") }}
        </div>

        <hr />

        <!-- Error -->
        <div v-if="error && error.message" data-test-id="meeting-settings-error">
            <hr />
            <l-text-alert type="danger" :text="error.message" />
            <hr />
        </div>
        <!--/Error -->

        <div class="row">
            <!-- Copy talking points -->
            <div class="col-md-12">
                <div
                    class="togglebutton"
                    style="margin-top: 10px"
                    data-test-id="meeting-copy-talking-points"
                >
                    <label for="meeting-copy-talking-points">
                        <input
                            id="meeting-copy-talking-points"
                            :checked="meeting.copyTalkingPoints !== 'disabled'"
                            type="checkbox"
                            :data-test-id="`meeting-copy-talking-points-enabled`"
                            @click="toggleCopyTalkingPoints()"
                        />
                        <span class="toggle"></span>
                        <span class="color-dark">{{
                            $t("meetings.optionCopyTalkingPointsEnabled")
                        }}</span>
                    </label>
                </div>
            </div>
            <!--/Copy talking points -->

            <!-- Toggle On/Off -->
            <div v-show="!isLinkedToGoogle" class="col-md-12">
                <l-space :size="1" />

                <div class="togglebutton" style="margin-top: 10px" data-test-id="meeting-on-off">
                    <label for="meeting-on">
                        <input
                            id="meeting-on"
                            :checked="meeting.cycle.frequency !== 'deactivated'"
                            type="checkbox"
                            :data-test-id="`meeting-on`"
                            @click="toggleOnOff()"
                        />
                        <span class="toggle"></span>
                        <span class="color-dark">{{
                            meeting.cycle.frequency === "deactivated"
                                ? $t("meetings.btnMeetingDisabled")
                                : $t("meetings.btnMeetingEnabled")
                        }}</span>
                    </label>
                </div>
            </div>
            <!--/Toggle On/Off -->
        </div>

        <hr />
        <div class="row tw-mb-2">
            <div class="col-md-12">
                <l-template-typeahead
                    :title="$t('meetings.labelSelectTemplate')"
                    :on-select="typeaheadTemplatesOnSelect"
                />

                <l-confirmation-modal
                    v-if="templateSelected"
                    :text="
                        `Are you sure you want to apply a meeting template &quot;${templateSelected.name}&quot;? This action is irreversible`
                    "
                    @confirm="applyTemplate()"
                    @cancel="cancelTemplate()"
                />
            </div>
        </div>
        <section>
            <div>
                <span class="agenda-question tw-mr-2">{{ $t("meetings.labelAgenda") }}</span>
                <i
                    class="fal color-grey"
                    :class="{ 'fa-sync fa-spin': updatingSection, 'fa-check': !updatingSection }"
                ></i>
                <span class="color-grey tw-ml-2">{{ $t("meetings.labelAutosaved") }}</span>
            </div>

            <l-space :size="1"></l-space>

            <div class="row">
                <div class="col-md-12">
                    <!-- Best practice sections -->
                    <div
                        v-if="
                            agendaQuestionIdeasEnabled &&
                                agendaQuestionIdeas &&
                                agendaQuestionIdeas.length
                        "
                    >
                        <div v-for="(category, categoryName) in groupedIdeas" :key="categoryName">
                            <div class="row" style="margin-bottom: 15px">
                                <div class="col-md-9">
                                    <strong>{{ categoryName }}</strong>
                                </div>
                            </div>

                            <div v-for="question in category" :key="question._id">
                                <div class="row">
                                    <div class="col-md-10">
                                        {{ question.name }}
                                    </div>
                                    <div class="col-md-2">
                                        <button
                                            class="btn btn-xs btn-primary pull-right"
                                            @click="
                                                () => {
                                                    addAgendaQuestion(question.name);
                                                    question.added = true;
                                                }
                                            "
                                        >
                                            <span v-if="question.added">
                                                <i class="fal fa-check"></i>
                                                <span class="l-default-margin-s">{{
                                                    $t("meetings.btnAdded")
                                                }}</span>
                                            </span>
                                            <span v-else>
                                                <i class="fal fa-plus"></i>
                                                <span class="l-default-margin-s">{{
                                                    $t("meetings.btnAdd")
                                                }}</span>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <hr />
                    </div>

                    <!-- End best practice sections -->

                    <div class="dropzone">
                        <draggable :list="agendaQuestions" @change="() => $forceUpdate()">
                            <transition-group>
                                <li
                                    v-for="(tempQuestion, index) in agendaQuestions"
                                    :key="index"
                                    class="mouseover-parent"
                                >
                                    <div
                                        v-if="
                                            tempQuestion._id !== 'actionItems' &&
                                                tempQuestion._id !== 'defaultSection'
                                        "
                                        class="input-group"
                                    >
                                        <l-input-text
                                            ref="question-title"
                                            v-model="agendaQuestions[index].name"
                                            wrapper-class="padding-clear"
                                            type="text"
                                            optional
                                            hide-help-block
                                            :label="''"
                                            :placeholder="$t(`meetings.placeholderQuestion`)"
                                            data-test-class="meeting-section-name"
                                            @input="verifyAgendaTemplateChange(index)"
                                        />
                                        <span class="input-group-addon" style="font-size: 16px">
                                            <i class="fal fa-arrows mouseover color-grey"></i>
                                        </span>
                                        <span
                                            class="input-group-addon"
                                            style="font-size: 16px"
                                            data-test-class="meeting-section-delete-btn"
                                            tabindex="0"
                                            @click="() => removeAgendaQuestion(index)"
                                            @keyup.enter="() => removeAgendaQuestion(index)"
                                        >
                                            <i class="fal fa-trash color-primary clickable"></i>
                                        </span>
                                    </div>
                                </li>
                            </transition-group>
                        </draggable>
                    </div>

                    <div class="input-group" style="padding-right: 5px; padding-left: 5px;">
                        <l-input-text
                            ref="question-title"
                            v-model="newQuestion.name"
                            wrapper-class="padding-clear"
                            type="text"
                            optional
                            hide-help-block
                            :label="''"
                            :placeholder="$t(`meetings.placeholderQuestion`)"
                            data-test-class="meeting-section-name"
                            @keyup.enter="addAgendaQuestion(newQuestion.name)"
                        />

                        <span
                            class="input-group-addon"
                            style="font-size: 18px; padding-left: 50px"
                            data-test-class="meeting-section-add-btn"
                            tabindex="0"
                            @click="() => addAgendaQuestion(newQuestion.name)"
                            @keyup.enter="() => addAgendaQuestion(newQuestion.name)"
                        >
                            <i class="fal fa-check color-primary clickable"></i>
                        </span>
                    </div>

                    <l-no-data
                        v-if="agendaQuestions.length === 2"
                        :icon="icon"
                        :text="$t(`meetings.noAgendaQuestions`)"
                    />

                    <l-space :size="1" />
                </div>
            </div>

            <button class="btn btn-default btn-raised" @click="showAgendaQuestionIdeas()">
                <span>{{ $t("meetings.btnShowIdeas") }}</span>
            </button>
        </section>
        <!--/Sections -->

        <hr />

        <button
            v-if="meeting.currentUserIsOwner"
            class="btn btn-default btn-raised"
            data-test-id="team-settings-update"
            @click="removeWarning = !removeWarning"
            @keyup.enter="removeWarning = !removeWarning"
        >
            {{ $t(`meetings.labelRemoveAll`) }}
        </button>

        <button class="btn btn-primary btn-raised pull-right" type="button" @click="closeSettings">
            {{ $t(`meetings.btnUpdateSettings`) }}
        </button>

        <l-confirmation-modal
            v-if="removeWarning"
            :text="$t('meetings.labelRemoveAllHelp')"
            @confirm="updateMeeting('remove')"
            @cancel="removeWarning = false"
        />

        <l-space :size="1" />
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import debounce from "lodash.debounce";
import format from "@/utils/format-typeahead";
import deepClone from "@/utils/deep-clone";

import Draggable from "vuedraggable";

import LDatePicker from "@/components/LDatePicker";
import LInputText from "@/components/LInputText";
import LInputTypeahead from "@/components/LInputTypeahead";
import LNoData from "@/components/LNoData";
import LSelect from "@/components/LSelect";
import LSpace from "@/components/LSpace";
import LTextAlert from "@/components/LTextAlert";
import LTimePicker from "@/components/LTimePicker";
import LGoogleCalendarEventSelect from "@/components/LGoogleCalendarEventSelect";

import icon from "@/img/icons/meetings.png";
import LConfirmationModal from "@/components/LConfirmationModal.vue";
import LTemplateTypeahead from "@/components/LMeetingTemplates/LTemplateTypeahead.vue";

const GOOGLE_AUTH_REDIRECT_KEY = "source";
const GOOGLE_AUTH_REDIRECT_VALUE = "googleAuth";

export default {
    components: {
        Draggable,

        LDatePicker,
        LInputText,
        LInputTypeahead,
        LNoData,
        LSelect,
        LSpace,
        LTextAlert,
        LTimePicker,
        LGoogleCalendarEventSelect,
        LConfirmationModal,
        LTemplateTypeahead,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            meeting: {},
            agendaQuestions: [],
            agendaQuestionIdeasEnabled: false,
            updatingCalendar: false,
            calendarURLCopied: false,
            updatingSection: false,
            updatingMeeting: false,
            removeWarning: false,
            error: null,
            newQuestion: { name: "" },
            templateSelected: null,
        };
    },
    computed: {
        ...mapGetters({
            getMeetingDetails: "meetings/meetings/details",
            agendaQuestionIdeas: "meetings/meetings/getAgendaQuestionIdeas",
            usersSearchList: "users/search/list",
            getUserList: "users/list/list",
            user: "users/current/user",
            teamSettings: "users/current/teamSettings",
            calendar: "calendar/url/get",
        }),

        groupedIdeas() {
            // group by categoryName
            return this.agendaQuestionIdeas.reduce(function(rv, x) {
                (rv[x["categoryName"]] = rv[x["categoryName"]] || []).push(x);
                return rv;
            }, {});
        },
        participants() {
            let list = Array.from(this.usersSearchList);

            if (!list.length) {
                list = this.getUserList({ key: "directTeam" });
            }

            return (
                list
                    // Filter current user
                    .filter(user => user._id !== this.user._id)
                    // Filter users that have already been selected
                    .filter(
                        user =>
                            this.meeting.otherUsers &&
                            !this.meeting.otherUsers.find(u => u._id === user._id),
                    )
                    // Format list to fit typeahead data requirements
                    .map(format.users)
            );
        },

        optionsFrequency() {
            return [
                { value: "manual", text: this.$t("meetings.optionManual") },
                { value: "daily", text: this.$t("meetings.optionDaily") },
                { value: "every week", text: this.$t("meetings.optionWeekly") },
                { value: "every 2 weeks", text: this.$t("meetings.optionBiweekly") },
                { value: "every month", text: this.$t("meetings.optionMonthly") },
                { value: "every 2 months", text: this.$t("meetings.option2Months") },
                { value: "every 3 months", text: this.$t("meetings.option3Months") },
                { value: "every 4 months", text: this.$t("meetings.option4Months") },
                { value: "every 6 months", text: this.$t("meetings.option6Months") },
                { value: "deactivated", text: this.$t("meetings.optionDeactivated") },
            ];
        },
        googleCalendarUsers() {
            return this.meeting.otherUsers.filter(user => user.source === "googleCalendar");
        },
        localUsers() {
            return this.meeting.otherUsers.filter(user => user.source === "local");
        },
        isLinkedToGoogle() {
            return Boolean(this.meeting.gCalEvent);
        },
        useMeridianTime() {
            return this.teamSettings.useMeridianTime;
        },
    },
    watch: {
        calendar: {
            immediate: true,
            handler(cal) {
                if (cal && cal.uri) {
                    this.calendarURLCopied = false;

                    this.calendar.url = `${window.location.origin}/api/calendar/subscribe/${cal.uri}`;
                    if (navigator.clipboard) {
                        navigator.clipboard.writeText(this.calendar.url).then(() => {
                            this.calendarURLCopied = true;
                        });
                    }
                }
            },
        },
        agendaQuestions: {
            deep: true,
            handler: debounce(function handler() {
                this.updateSection();
            }, 1000),
        },
        meeting: {
            deep: true,
            handler: debounce(function handler() {
                this.updateMeeting();
            }, 1000),
        },
    },
    created() {
        this.icon = icon;
        this.meeting = this.getMeetingDetails(this.id);
        this.agendaQuestions = deepClone(this.meeting.agendaQuestions);

        if (this.$route.query[GOOGLE_AUTH_REDIRECT_KEY] === GOOGLE_AUTH_REDIRECT_VALUE) {
            this.$store.dispatch("auth/performSessionRefresh");
        }
    },
    methods: {
        async authGoogleCalendar() {
            this.$store.dispatch("googleCalendar/calendar/startAuth", {
                redirect: `/app/#/meetings/details/${this.id}/settings?${GOOGLE_AUTH_REDIRECT_KEY}=${GOOGLE_AUTH_REDIRECT_VALUE}`,
            });
        },
        goToEvent() {
            window.location = this.meeting.gCalEvent.htmlLink;
        },
        showAgendaQuestionIdeas() {
            if (this.agendaQuestionIdeasEnabled) {
                this.agendaQuestionIdeasEnabled = false;
                return;
            }
            this.agendaQuestionIdeasEnabled = true;

            if (!this.agendaQuestionIdeas || !this.agendaQuestionIdeas.length) {
                this.$store.dispatch({ type: "meetings/meetings/getAgendaQuestionIdeas" });
            }
        },
        async linkGoogleCalendar(gCalEvent) {
            const emails = (gCalEvent.attendees || [])
                .map(attendee => attendee.email)
                .filter(email => email != this.user.username);
            const gCalUsers = await this.$store.dispatch("users/search/findByEmailBatch", {
                emails,
            });

            // extend existing participants with google calendar participants:
            // - if Google Calendar event participant is already a meeting participant - keep it,
            //   but mark it as coming from googleCalendar
            // - if Google Calendar event participant is not yet a meeting participant - add it
            // - do not remove existing meeting participants
            gCalUsers.forEach(gCalUser => {
                const newUser = { ...gCalUser, source: "googleCalendar" };
                let replaced = false;
                this.meeting.otherUsers = this.meeting.otherUsers.map(existingUser => {
                    if (existingUser._id == newUser._id) {
                        replaced = true;
                        return newUser;
                    }
                    return existingUser;
                });
                if (!replaced) {
                    this.meeting.otherUsers.push(newUser);
                }
            });

            this.meeting.title = gCalEvent.summary;
            this.meeting.gCalEvent = gCalEvent;
            this.meeting.cycle.duration = this.$moment(gCalEvent.end.dateTime ?? gCalEvent.end.date)
                .diff(gCalEvent.start.dateTime ?? gCalEvent.start.date, "minutes")
                .toString();
            this.meeting.cycle.nextTime = gCalEvent.start.dateTime ?? gCalEvent.start.date;
            await this.$store
                .dispatch({
                    type: "meetings/meetings/update",
                    payload: {
                        data: {
                            _id: this.meeting._id,
                            groupToken: this.meeting.groupToken,
                            gCalEvent: this.meeting.gCalEvent,
                            gCalAction: "link",
                        },
                    },
                })
                .catch(err => {
                    if (err?.response?.data?.message) {
                        this.error = { message: err.response.data.message };
                    }
                });
        },
        async unlinkGoogleCalendar() {
            this.meeting.otherUsers = this.meeting.otherUsers.map(user => ({
                ...user,
                source: "local",
            }));

            await this.$store
                .dispatch({
                    type: "meetings/meetings/update",
                    payload: {
                        data: {
                            _id: this.meeting._id,
                            gCalEvent: this.meeting.gCalEvent,
                            gCalAction: "unlink",
                        },
                    },
                })
                .catch(err => {
                    if (err?.response?.data?.message) {
                        this.error = { message: err.response.data.message };
                    }
                });
            this.meeting.gCalEvent = null;
        },
        googleEventLinkError() {
            this.error = { message: this.$t(`meetings.noLinkableEventsFound`) };
        },
        addAgendaQuestion(name) {
            const order = this.agendaQuestions.length + 1;
            this.agendaQuestions.push({ name, type: "checkinQuestionCustom", order });
            this.newQuestion.name = "";
        },
        removeAgendaQuestion(index) {
            if (index >= 0) {
                this.agendaQuestions.splice(index, 1);
            }
        },
        verifyAgendaTemplateChange(index) {
            // if a user wants to change template questions, we should generate a new question (no ID, different type)

            if (this.agendaQuestions[index].type !== "checkinQuestionCustom") {
                this.agendaQuestions[index]._id = null;
                this.agendaQuestions[index].type = "checkinQuestionCustom";
            }
        },
        async updateSection() {
            this.updatingSection = true;

            await this.$store.dispatch({
                type: "meetings/sections/update",
                payload: {
                    data: {
                        questions: this.agendaQuestions,
                        scope: "checkinQuestionCustom",
                        _id: this.meeting._id,
                    },
                },
            });

            await this.$store.dispatch({
                type: "meetings/meetings/getDetails",
                payload: { _id: this.meeting._id },
            });

            this.updatingSection = false;
        },

        typeaheadTemplatesOnSelect(selected) {
            this.templateSelected = selected;
        },

        applyTemplate() {
            this.agendaQuestions = [];
            this.templateSelected.sections.forEach(section => this.addAgendaQuestion(section.name));
            this.templateSelected = null;
        },

        cancelTemplate() {
            this.templateSelected = null;
        },

        typeaheadParticipantsOnInputChange(query) {
            if (query.length >= 2) {
                this.$store.dispatch("users/search/findByName", { query });
            } else {
                this.$store.dispatch("users/search/reset");
            }
        },
        typeaheadParticipantsOnSelect(selected) {
            this.meeting.otherUsers.push({ ...selected, source: "local" });
        },
        removeParticipant(selected) {
            this.meeting.otherUsers = this.meeting.otherUsers.filter(u => u._id !== selected._id);
        },

        updateNextDate(date) {
            if (
                this.$moment(this.meeting.cycle.nextTime).isValid() &&
                this.$moment(date).isValid()
            ) {
                const d = new Date(this.meeting.cycle.nextTime);
                d.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());

                this.meeting.cycle.nextTime = d;
            }
        },
        updateNextTime(time) {
            if (this.$moment(time).isValid()) {
                this.meeting.cycle.nextTime = new Date(time);
            }
        },

        async createCalendar() {
            this.updatingCalendar = true;

            await this.$store.dispatch({ type: "calendar/url/create" });

            this.updatingCalendar = false;
        },

        toggleOnOff() {
            if (this.meeting.cycle.frequency !== "deactivated") {
                this.meeting.cycle.frequency = "deactivated";
                return;
            }

            this.meeting.cycle.frequency = "every week";
        },

        toggleCopyTalkingPoints() {
            if (this.meeting.copyTalkingPoints !== "disabled") {
                this.meeting.copyTalkingPoints = "disabled";
                return;
            }

            this.meeting.copyTalkingPoints = "enabled";
        },

        async updateMeeting(scope) {
            this.updatingMeeting = true;
            this.error = null;

            const cycle = this.meeting.cycle;

            // Add timezone information
            if (cycle) {
                cycle.tz = Intl?.DateTimeFormat?.().resolvedOptions?.()?.timeZone ?? cycle.tz;
            }

            const participants = [
                ...new Set([this.user._id, ...this.meeting.otherUsers?.map(({ _id }) => _id)]),
            ];

            var data = {
                _id: this.meeting._id,
                groupToken: this.meeting.groupToken,
                participants,
                cycle,
                title: this.meeting.title,
                copyTalkingPoints: this.meeting.copyTalkingPoints,
            };

            if (scope === "remove") {
                data.status = "removed";
            }

            await this.$store
                .dispatch({
                    type: "meetings/meetings/update",
                    payload: { data },
                })
                .catch(err => {
                    if (err?.response?.data?.message) {
                        this.error = { message: err.response.data.message };
                    }
                });

            if (scope === "remove") {
                this.$router.push("/meetings/list");
            }

            this.updatingMeeting = false;
        },
        closeSettings() {
            this.$router.replace(`/meetings/details/${this.meeting._id}`);
        },
    },
};
</script>

<style scoped>
.agenda-question .btn {
    margin-left: 15px;
    margin-top: 0px;
    margin-bottom: 0px;
}
.other-users-container {
    margin-bottom: 10px;
    display: inline-flex;
    flex-wrap: wrap;
}
</style>
